import React, { useState } from 'react';
import {
  Box,
  Alert,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Typography,
} from '@mui/material';
import { SwapVert, ContentCopy, AccessTime } from '@mui/icons-material';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';

const TimestampUtility: React.FC = () => {
  const [timestamp, setTimestamp] = useState(() => localStorage.getItem('timestamp-unix') || '');
  const [dateString, setDateString] = useState(() => localStorage.getItem('timestamp-date') || '');
  const [unit, setUnit] = useState<'seconds' | 'milliseconds'>(() =>
    (localStorage.getItem('timestamp-unit') as 'seconds' | 'milliseconds') || 'seconds'
  );
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    localStorage.setItem('timestamp-unix', timestamp);
    localStorage.setItem('timestamp-date', dateString);
    localStorage.setItem('timestamp-unit', unit);
  }, [timestamp, dateString, unit]);

  const getCurrentTimestamp = () => {
    const now = Date.now();
    const ts = unit === 'seconds' ? Math.floor(now / 1000) : now;
    setTimestamp(ts.toString());
    setError('');
  };

  const timestampToDate = () => {
    try {
      const ts = parseInt(timestamp, 10);
      if (isNaN(ts)) {
        setError('Invalid timestamp');
        return;
      }
      const ms = unit === 'seconds' ? ts * 1000 : ts;
      const date = new Date(ms);
      if (isNaN(date.getTime())) {
        setError('Invalid timestamp');
        return;
      }
      setDateString(date.toISOString());
      setError('');
    } catch (err) {
      setError('Invalid timestamp');
    }
  };

  const dateToTimestamp = () => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        setError('Invalid date format');
        return;
      }
      const ms = date.getTime();
      const ts = unit === 'seconds' ? Math.floor(ms / 1000) : ms;
      setTimestamp(ts.toString());
      setError('');
    } catch (err) {
      setError('Invalid date format');
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const clearAll = () => {
    setTimestamp('');
    setDateString('');
    setError('');
  };

  const formatLocalDate = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleString();
    } catch {
      return '';
    }
  };

  return (
    <Box>
      <UtilityHeader 
        title="Timestamp Converter" 
        description="Seamlessly convert between Unix epochs and ISO 8601 dates." 
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}

      {copied && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
          Copied to clipboard!
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Time Unit
        </Typography>
        <ToggleButtonGroup
          value={unit}
          exclusive
          onChange={(_, newUnit) => newUnit && setUnit(newUnit)}
          size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.03)' }}
        >
          <ToggleButton value="seconds" sx={{ px: 3 }}>Seconds</ToggleButton>
          <ToggleButton value="milliseconds" sx={{ px: 3 }}>Milliseconds</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <StyledTextArea
              label="Unix Timestamp"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              placeholder={unit === 'seconds' ? '1706400000' : '1706400000000'}
            />
            <Stack direction="row" spacing={1}>
              <GlowButton
                onClick={timestampToDate}
                disabled={!timestamp.trim()}
                startIcon={<SwapVert />}
              >
                To Date
              </GlowButton>
              <GlowButton
                variant="outlined"
                disableGlow
                onClick={getCurrentTimestamp}
                startIcon={<AccessTime />}
              >
                Now
              </GlowButton>
              <GlowButton
                variant="text"
                disableGlow
                onClick={() => handleCopy(timestamp)}
                disabled={!timestamp.trim()}
                startIcon={<ContentCopy />}
                sx={{ color: 'text.secondary' }}
              >
                Copy
              </GlowButton>
            </Stack>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <StyledTextArea
              label="ISO 8601 / Human Readable"
              accentColor="secondary"
              value={dateString}
              onChange={(e) => setDateString(e.target.value)}
              placeholder="2024-01-28T00:00:00.000Z"
              helperText={dateString && formatLocalDate(dateString) ? `Local: ${formatLocalDate(dateString)}` : ''}
            />
            <Stack direction="row" spacing={1}>
              <GlowButton
                color="secondary"
                glowColor="secondary"
                onClick={dateToTimestamp}
                disabled={!dateString.trim()}
                startIcon={<SwapVert />}
              >
                To Epoch
              </GlowButton>
              <GlowButton
                variant="text"
                disableGlow
                onClick={() => handleCopy(dateString)}
                disabled={!dateString.trim()}
                startIcon={<ContentCopy />}
                sx={{ color: 'text.secondary' }}
              >
                Copy
              </GlowButton>
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ mt: 6, display: 'flex', justifyContent: 'flex-end' }}>
        <GlowButton
          variant="outlined"
          disableGlow
          color="inherit"
          onClick={clearAll}
          disabled={!timestamp.trim() && !dateString.trim()}
          sx={{ opacity: 0.6 }}
        >
          Clear All
        </GlowButton>
      </Box>
    </Box>
  );
};

export default TimestampUtility;
